import { useState } from "react";
import {
  editSocialUser,
  executeMutation,
  executeQuery,
} from "../../../../../graphql/client";

import { API_BASE_URL } from "../../constants";

// #134 phase 8: pending-requests reads + approvals via graphql (were
// REST ?type=pendingJoinRequests / the approvals POST). deleteUsers
// stays REST until its own slice.
const PENDING_REQUESTS_QUERY = `
  query PendingJoinRequests {
    pendingJoinRequests {
      groupmeData { nickname id user_id state timestamp image_url phone }
      matchCount
      hasConflict
    }
  }
`;

const PHONE_MATCHES_QUERY = `
  query PendingPhoneMatches($phoneNumbers: [String!]!) {
    pendingPhoneMatches(phoneNumbers: $phoneNumbers)
  }
`;

const APPROVE_PENDING_MUTATION = `
  mutation ApprovePendingRequests($approvals: [PendingApprovalInput!]!) {
    approvePendingRequests(approvals: $approvals) {
      success
      message
      groupmeId
      chosenUserId
      error
    }
  }
`;

const toApprovalInput = (a) => ({
  groupmeId: a.groupmeId,
  groupmeUserId: a.groupmeUserId,
  chosenUserId: a.chosenUserId,
  customMergeFieldsJson:
    a.customMergeFields && Object.keys(a.customMergeFields).length
      ? JSON.stringify(a.customMergeFields)
      : null,
});

/**
 * Custom hook for managing pending user requests
 * @param {Object} props - Hook configuration
 * @param {Function} props.getAuthHeaders - Function to get authentication headers
 * @param {Function} props.logout - Function to handle logout
 * @returns {Object} Pending requests state and methods
 */
const usePendingRequests = ({ getAuthHeaders, logout }) => {
  // State
  const [pendingUsers, setPendingUsers] = useState([]);
  const [selectedApprovals, setSelectedApprovals] = useState({});
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  // Editable user state
  const [editableUser, setEditableUser] = useState(null);
  const [editedFields, setEditedFields] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState(null);

  // Merge functionality state
  const [isMergeMode, setIsMergeMode] = useState(false);
  const [recordsToMerge, setRecordsToMerge] = useState([]);
  const [baseRecordId, setBaseRecordId] = useState(null);
  const [mergedData, setMergedData] = useState({});
  const [groupmeDataForMerge, setGroupmeDataForMerge] = useState(null);

  // Track request status to prevent duplicate in-flight requests
  let pendingRequestInProgress = false;
  let phoneMatchRequestInProgress = false;

  /**
   * Load pending requests from the API
   * Added debounce protection to prevent multiple concurrent calls
   */
  const loadPendingRequests = async () => {
    // Prevent multiple concurrent requests
    if (pendingRequestInProgress) {
      console.log(
        "Skipping duplicate pending requests API call - already in progress"
      );
      return;
    }

    setLoading(true);
    setErrorMessage("");
    pendingRequestInProgress = true;

    try {
      console.log("Loading pending join requests via graphql");
      const data = await executeQuery(PENDING_REQUESTS_QUERY);
      // matchingDbUsers starts empty (matchCount -1 = not fetched);
      // the on-demand phone-match load fills it in.
      const withEmptyMatches = (data?.pendingJoinRequests || []).map(
        (r) => ({ ...r, matchingDbUsers: [] })
      );
      // Sort pending users by timestamp (most recent first)
      const sortedUsers = withEmptyMatches.sort((a, b) => {
        const timestampA = a.groupmeData?.timestamp || 0;
        const timestampB = b.groupmeData?.timestamp || 0;
        return timestampB - timestampA; // Descending order (most recent first)
      });
      setPendingUsers(sortedUsers);
    } catch (error) {
      console.error("Error loading pending requests:", error);
      setErrorMessage("Failed to load pending requests: " + error.message);
    } finally {
      setLoading(false);
      pendingRequestInProgress = false;
    }
  };

  /**
   * Load database matches for specific phone numbers
   */
  const loadMatchesForPhones = async (phoneNumbers) => {
    // Prevent multiple concurrent requests
    if (phoneMatchRequestInProgress) {
      console.log("Phone match request already in progress");
      return null;
    }

    phoneMatchRequestInProgress = true;

    try {
      console.log("Loading DB matches for phones via graphql");
      const data = await executeQuery(PHONE_MATCHES_QUERY, {
        phoneNumbers,
      });
      return data?.pendingPhoneMatches
        ? JSON.parse(data.pendingPhoneMatches)
        : {};
    } catch (error) {
      console.error("Error loading phone matches:", error);
      setErrorMessage("Failed to load database matches: " + error.message);
      return null;
    } finally {
      phoneMatchRequestInProgress = false;
    }
  };

  /**
   * Load matches for a specific user
   */
  const loadMatchesForUser = async (user) => {
    const phoneNumber = user.groupmeData.phone;
    const matchResults = await loadMatchesForPhones([phoneNumber]);

    if (matchResults && matchResults[phoneNumber]) {
      // Update the user in the pendingUsers state while maintaining sort order
      setPendingUsers(prevUsers => {
        const updatedUsers = prevUsers.map(u =>
          u.groupmeData.id === user.groupmeData.id
            ? {
                ...u,
                matchingDbUsers: matchResults[phoneNumber].matchingDbUsers,
                hasConflict: matchResults[phoneNumber].hasConflict,
                matchCount: matchResults[phoneNumber].matchCount,
              }
            : u
        );
        // Maintain the timestamp sort order (most recent first)
        return updatedUsers.sort((a, b) => {
          const timestampA = a.groupmeData?.timestamp || 0;
          const timestampB = b.groupmeData?.timestamp || 0;
          return timestampB - timestampA;
        });
      });
      return matchResults[phoneNumber];
    }
    return null;
  };

  /**
   * Handle selection of a user for approval
   * Toggle selection if the user is already selected
   */
  const handleSelectUserForApproval = (groupmeId, groupmeUserId, userId) => {
    setSelectedApprovals((prevApprovals) => {
      // Check if this groupmeId is already in selectedApprovals with the same userId
      if (prevApprovals[groupmeId]?.chosenUserId === userId) {
        // If it's already selected, remove it (toggle off)
        const newApprovals = { ...prevApprovals };
        delete newApprovals[groupmeId];
        return newApprovals;
      } else {
        // Otherwise select/update it
        return {
          ...prevApprovals,
          [groupmeId]: {
            groupmeId,
            groupmeUserId,
            chosenUserId: userId,
          },
        };
      }
    });
  };

  /**
   * Toggle edit mode for a user
   */
  const toggleEditMode = (dbUser) => {
    if (editableUser && editableUser.userId === dbUser.userId) {
      // Exit edit mode
      setEditableUser(null);
      setEditedFields({});
      setSaveMessage(null);
    } else {
      // Enter edit mode
      setEditableUser(dbUser);
      setEditedFields({});
      setSaveMessage(null);
    }
  };

  /**
   * Handle field change in edit mode
   */
  const handleFieldChange = (field, value) => {
    setEditedFields((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  /**
   * Save edited user data
   */
  const saveUserData = async (groupmeId, userId) => {
    // If no changes made, exit early
    if (Object.keys(editedFields).length === 0) {
      setSaveMessage({ type: "info", text: "No changes made" });
      return;
    }

    setIsSaving(true);
    setSaveMessage(null);

    try {
      let headers = { "Content-Type": "application/json" };

      const authHeaders = getAuthHeaders();
      headers = { ...headers, ...authHeaders };

      // Process fields before sending
      const processedFields = { ...editedFields };

      // Convert arrays to comma-separated strings
      if (
        processedFields.reInterestedInEvents &&
        Array.isArray(processedFields.reInterestedInEvents)
      ) {
        processedFields.reInterestedInEvents =
          processedFields.reInterestedInEvents.join(", ");
      }

      if (
        processedFields.selectedEvents &&
        Array.isArray(processedFields.selectedEvents)
      ) {
        processedFields.selectedEvents =
          processedFields.selectedEvents.join(", ");
      }

      // #134 phase 5: patch via graphql editSocialUser (was REST
      // editUser; groupmeId was accepted but unused server-side).
      const result = await editSocialUser(userId, processedFields);
      if (!result.success) {
        throw new Error("Failed to update user data");
      }

      setSaveMessage({
        type: "success",
        text: result.message || "User data updated successfully",
      });

      // Exit edit mode
      setEditableUser(null);
      setEditedFields({});

      // Reload the list to see updated data
      loadPendingRequests();
    } catch (error) {
      setSaveMessage({
        type: "error",
        text: `Failed to update user: ${error.message}`,
      });
    } finally {
      setIsSaving(false);
    }
  };

  /**
   * Handle starting merge process
   */
  const startMergeProcess = (user) => {
    if (user.matchingDbUsers.length < 2) {
      setErrorMessage("At least two database records are required for merging");
      return;
    }

    setRecordsToMerge(user.matchingDbUsers);
    setBaseRecordId(user.matchingDbUsers[0].userId);
    setGroupmeDataForMerge(user.groupmeData);

    // Initialize merged data with default record
    initializeMergedData(user.matchingDbUsers);

    setIsMergeMode(true);
  };

  /**
   * Initialize merged data with smart defaults
   */
  const initializeMergedData = (records) => {
    const baseRecord = records[0];
    const initialMergedData = { ...baseRecord };

    // Special handling for arrays that should be combined
    let combinedReInterestedInEvents = [];
    let combinedSelectedEvents = [];

    // Track fields with conflicts
    const fieldsWithConflicts = new Set();

    // Find conflicting fields
    const allFields = new Set();
    records.forEach((record) => {
      Object.keys(record).forEach((key) => {
        if (key !== "userId") {
          allFields.add(key);
        }
      });
    });

    // Check each field for conflicts
    allFields.forEach((field) => {
      // Skip arrays that we combine
      if (field === "reInterestedInEvents" || field === "selectedEvents") {
        return;
      }

      let valuesSeen = new Set();

      // Get all non-empty values for this field
      records.forEach((record) => {
        const value = record[field];
        if (value !== null && value !== undefined && value !== "") {
          if (typeof value === "boolean") {
            valuesSeen.add(value.toString()); // Convert boolean to string for Set
          } else {
            valuesSeen.add(value);
          }
        }
      });

      // If there's more than one non-empty value, we have a conflict
      if (valuesSeen.size > 1) {
        fieldsWithConflicts.add(field);
      }
    });

    // Process all records
    records.forEach((record) => {
      // Combine arrays
      if (record.reInterestedInEvents) {
        // Check if it's already an array or if it's a string that needs parsing
        let eventArray = [];
        if (Array.isArray(record.reInterestedInEvents)) {
          eventArray = record.reInterestedInEvents;
        } else if (typeof record.reInterestedInEvents === "string") {
          // Try to parse if it's a JSON string
          try {
            eventArray = JSON.parse(record.reInterestedInEvents);
            if (!Array.isArray(eventArray)) {
              eventArray = record.reInterestedInEvents
                .split(",")
                .map((item) => item.trim());
            }
          } catch (e) {
            // If parsing fails, treat it as comma-separated string
            eventArray = record.reInterestedInEvents
              .split(",")
              .map((item) => item.trim());
          }
        }
        combinedReInterestedInEvents = [
          ...combinedReInterestedInEvents,
          ...eventArray,
        ];
      }

      if (record.selectedEvents) {
        // Same approach for selectedEvents
        let eventsArray = [];
        if (Array.isArray(record.selectedEvents)) {
          eventsArray = record.selectedEvents;
        } else if (typeof record.selectedEvents === "string") {
          try {
            eventsArray = JSON.parse(record.selectedEvents);
            if (!Array.isArray(eventsArray)) {
              eventsArray = record.selectedEvents
                .split(",")
                .map((item) => item.trim());
            }
          } catch (e) {
            eventsArray = record.selectedEvents
              .split(",")
              .map((item) => item.trim());
          }
        }
        combinedSelectedEvents = [...combinedSelectedEvents, ...eventsArray];
      }

      // For other fields, take non-empty values over empty ones if there's no conflict
      Object.keys(record).forEach((key) => {
        if (
          key !== "userId" &&
          key !== "reInterestedInEvents" &&
          key !== "selectedEvents"
        ) {
          // For fields with conflicts, just keep the base record value
          if (fieldsWithConflicts.has(key)) {
            return;
          }

          // Special handling for boolean values - they need to be copied even if false
          if (typeof record[key] === "boolean") {
            // If this is the only record with this field defined, use its value
            const recordsWithThisField = records.filter(
              (r) => typeof r[key] === "boolean"
            );
            if (recordsWithThisField.length === 1) {
              initialMergedData[key] = record[key];
            }
            // If there are multiple records with different values, it would be caught by fieldsWithConflicts
          }
          // For non-boolean values, take non-empty over empty
          else if (
            (initialMergedData[key] === null ||
              initialMergedData[key] === undefined ||
              initialMergedData[key] === "") &&
            record[key] !== null &&
            record[key] !== undefined &&
            record[key] !== ""
          ) {
            initialMergedData[key] = record[key];
          }
        }
      });
    });

    // De-duplicate arrays
    initialMergedData.reInterestedInEvents = [
      ...new Set(combinedReInterestedInEvents),
    ];
    initialMergedData.selectedEvents = [...new Set(combinedSelectedEvents)];

    setMergedData(initialMergedData);
  };

  /**
   * Change the base record for merging
   */
  const changeBaseRecord = (recordId) => {
    setBaseRecordId(recordId);

    // Re-initialize with new base record
    const newBaseRecord = recordsToMerge.find(
      (record) => record.userId === recordId
    );
    if (newBaseRecord) {
      const otherRecords = recordsToMerge.filter(
        (record) => record.userId !== recordId
      );
      initializeMergedData([newBaseRecord, ...otherRecords]);
    }
  };

  /**
   * Update a specific field in the merged data
   */
  const updateMergedField = (field, value) => {
    setMergedData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  /**
   * Select a field value from a specific record
   */
  const selectFieldFromRecord = (field, recordId) => {
    const record = recordsToMerge.find((r) => r.userId === recordId);
    if (record && field in record) {
      // Special handling for arrays that should be combined
      if (field === "reInterestedInEvents" || field === "selectedEvents") {
        // Don't replace, combine with existing values
        const currentValues = mergedData[field] || [];

        // Parse record values, handling both arrays and strings
        let recordValues = [];
        if (Array.isArray(record[field])) {
          recordValues = record[field];
        } else if (typeof record[field] === "string") {
          try {
            const parsed = JSON.parse(record[field]);
            recordValues = Array.isArray(parsed)
              ? parsed
              : record[field].split(",").map((item) => item.trim());
          } catch (e) {
            recordValues = record[field].split(",").map((item) => item.trim());
          }
        }

        const combinedValues = [
          ...new Set([...currentValues, ...recordValues]),
        ];

        updateMergedField(field, combinedValues);
      }
      // Special handling for boolean values
      else if (typeof record[field] === "boolean") {
        // For booleans, copy the exact value (including false)
        updateMergedField(field, record[field]);
      } else {
        // Normal field, just select the value
        updateMergedField(field, record[field]);
      }
    }
  };

  /**
   * Save the merged record
   */
  const saveMergedRecord = async () => {
    setIsSaving(true);
    setSaveMessage(null);

    try {
      let headers = { "Content-Type": "application/json" };

      const authHeaders = getAuthHeaders();
      headers = { ...headers, ...authHeaders };

      // Process merged data before sending
      const processedData = { ...mergedData };

      // Convert arrays to comma-separated strings
      if (
        processedData.reInterestedInEvents &&
        Array.isArray(processedData.reInterestedInEvents)
      ) {
        processedData.reInterestedInEvents =
          processedData.reInterestedInEvents.join(", ");
      }

      if (
        processedData.selectedEvents &&
        Array.isArray(processedData.selectedEvents)
      ) {
        processedData.selectedEvents = processedData.selectedEvents.join(", ");
      }

      // First save the merged data to the base record (#134 phase 5:
      // graphql editSocialUser).
      await editSocialUser(baseRecordId, processedData);

      // Now handle the GroupMe approval using the base record
      // (#134 phase 8: graphql approvePendingRequests).
      await executeMutation(APPROVE_PENDING_MUTATION, {
        approvals: [
          {
            groupmeId: groupmeDataForMerge.id,
            groupmeUserId: groupmeDataForMerge.user_id,
            chosenUserId: baseRecordId,
          },
        ],
      });

      // Now delete the other records
      const recordsToDelete = recordsToMerge
        .filter((record) => record.userId !== baseRecordId)
        .map((record) => record.userId);

      if (recordsToDelete.length > 0) {
        // #134 phase 9: graphql deleteSocialUsers (was REST
        // deleteUsers). Not critical — a failure just leaves the
        // redundant rows behind, so log and continue.
        try {
          await executeMutation(
            `mutation DeleteSocialUsers($userIds: [ID!]!) {
              deleteSocialUsers(userIds: $userIds) { userId success message }
            }`,
            { userIds: recordsToDelete }
          );
        } catch (delErr) {
          console.warn("Could not delete redundant records:", delErr);
        }
      }

      setSaveMessage({
        type: "success",
        text: "Records merged successfully and user approved",
      });

      // Exit merge mode
      setIsMergeMode(false);
      setRecordsToMerge([]);
      setBaseRecordId(null);
      setMergedData({});

      // Reload the list to see updated data
      loadPendingRequests();
    } catch (error) {
      setSaveMessage({
        type: "error",
        text: `Failed to merge records: ${error.message}`,
      });
    } finally {
      setIsSaving(false);
    }
  };

  /**
   * Cancel merge process
   */
  const cancelMerge = () => {
    setIsMergeMode(false);
    setRecordsToMerge([]);
    setBaseRecordId(null);
    setMergedData({});
    setGroupmeDataForMerge(null);
    setSaveMessage(null);
  };

  /**
   * Handle submitting approvals
   */
  const handleSubmitApprovals = async () => {
    setLoading(true);
    setErrorMessage("");
    setSuccessMessage("");

    try {
      let headers = getAuthHeaders();

      // Convert selectedApprovals object to array
      const approvalsArray = Object.values(selectedApprovals);

      if (approvalsArray.length === 0) {
        setErrorMessage("Please select users to approve");
        setLoading(false);
        return;
      }

      // #134 phase 8: graphql approvePendingRequests.
      await executeMutation(APPROVE_PENDING_MUTATION, {
        approvals: approvalsArray.map(toApprovalInput),
      });
      setSuccessMessage(
        `Successfully approved ${approvalsArray.length} user(s)!`
      );

      // Clear approvals and reload
      setSelectedApprovals({});
      loadPendingRequests();
    } catch (error) {
      setErrorMessage("Failed to submit approvals: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  return {
    // State
    pendingUsers,
    selectedApprovals,
    loading,
    errorMessage,
    successMessage,
    editableUser,
    editedFields,
    isSaving,
    saveMessage,
    isMergeMode,
    recordsToMerge,
    baseRecordId,
    mergedData,
    groupmeDataForMerge,

    // Functions
    loadPendingRequests,
    loadMatchesForUser,
    loadMatchesForPhones,
    handleSelectUserForApproval,
    toggleEditMode,
    handleFieldChange,
    saveUserData,
    startMergeProcess,
    changeBaseRecord,
    updateMergedField,
    selectFieldFromRecord,
    saveMergedRecord,
    cancelMerge,
    handleSubmitApprovals,
    setErrorMessage,
    setSuccessMessage,
  };
};

export default usePendingRequests;
